// Outils de base sur l'état du monde. Implémenté par l'intégrateur — ne pas réécrire,
// vous pouvez seulement AJOUTER des fonctions en bas du fichier si vraiment nécessaire.
import type {
  Character, Edge, GameEvent, GameState, Id, Location, Millis, TonePreset, CanonFact,
} from "../shared/types";
import { HOUR } from "../shared/types";

export const MAX_EVENTS = 400;
export const MAX_CHAT = 200;

export function newId(state: GameState, prefix: string): Id {
  state.idCounter += 1;
  return `${prefix}_${state.idCounter.toString(36)}`;
}

export function createEmptyState(args: {
  worldId: Id; code: string; name: string; pitch: string; tone: TonePreset; now: Millis; seed: number;
}): GameState {
  return {
    version: 1,
    worldId: args.worldId,
    code: args.code,
    name: args.name,
    createdAt: args.now,
    rngState: args.seed >>> 0,
    idCounter: 0,
    setup: { pitch: args.pitch, tone: args.tone },
    status: "genese",
    statusMessage: "Le Conteur tisse le monde…",
    bible: null,
    regions: [],
    locations: {},
    edges: [],
    npcs: {},
    factions: [],
    clocks: {},
    quests: {},
    canon: [],
    players: {},
    characters: {},
    graveyard: [],
    parties: {},
    combats: {},
    dungeons: {},
    dungeonRuns: {},
    sollicitations: {},
    chapters: [],
    events: [],
    chat: [],
    lastDirectorAt: args.now,
    lastChronicleAt: args.now,
    lastTickAt: args.now,
  };
}

/** Crée un événement ET l'ajoute au fil du monde (tronqué à MAX_EVENTS). */
export function makeEvent(state: GameState, now: Millis, e: Omit<GameEvent, "id" | "at">): GameEvent {
  const ev: GameEvent = { id: newId(state, "ev"), at: now, ...e };
  state.events.push(ev);
  if (state.events.length > MAX_EVENTS) state.events.splice(0, state.events.length - MAX_EVENTS);
  return ev;
}

/** Visibilité « seulement ce personnage ». */
export const toChar = (id: Id) => ({ scope: "characters" as const, characterIds: [id] });
export const toChars = (ids: Id[]) => ({ scope: "characters" as const, characterIds: ids });
export const atLocation = (locationId: Id) => ({ scope: "location" as const, locationId });
export const toAll = () => ({ scope: "global" as const });

export function getCharacter(state: GameState, id: Id | null | undefined): Character | undefined {
  return id ? state.characters[id] : undefined;
}

export function characterOfPlayer(state: GameState, playerId: Id): Character | undefined {
  const p = state.players[playerId];
  return p?.characterId ? state.characters[p.characterId] : undefined;
}

export function isAlive(c: Character): boolean {
  return c.lifeState !== "mort";
}

/** Personnages présents dans un lieu (pas en voyage, pas morts, pas égarés). */
export function charactersAt(state: GameState, locationId: Id): Character[] {
  return Object.values(state.characters).filter(
    (c) => c.locationId === locationId && !c.travel && c.lifeState !== "mort" && c.lifeState !== "egare",
  );
}

export function normalize(s: string): string {
  return s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

/** Retrouve un lieu par id ou par nom (insensible à la casse et aux accents, « le/la » ignorés). */
export function locationByRef(state: GameState, ref: string | null | undefined): Location | undefined {
  if (!ref) return undefined;
  if (state.locations[ref]) return state.locations[ref];
  const n = normalize(ref).replace(/^(le|la|les|l) /, "");
  const all = Object.values(state.locations);
  return (
    all.find((l) => normalize(l.name) === n) ??
    all.find((l) => normalize(l.name).replace(/^(le|la|les|l) /, "") === n) ??
    all.find((l) => normalize(l.name).includes(n) && n.length >= 4)
  );
}

export function npcByRef(state: GameState, ref: string | null | undefined) {
  if (!ref) return undefined;
  if (state.npcs[ref]) return state.npcs[ref];
  const n = normalize(ref);
  return Object.values(state.npcs).find((p) => normalize(p.name) === n);
}

export function edgeBetween(state: GameState, a: Id, b: Id): Edge | undefined {
  return state.edges.find((e) => (e.a === a && e.b === b) || (e.a === b && e.b === a));
}

export function neighbors(state: GameState, locationId: Id): { location: Location; edge: Edge }[] {
  const out: { location: Location; edge: Edge }[] = [];
  for (const e of state.edges) {
    const other = e.a === locationId ? e.b : e.b === locationId ? e.a : null;
    if (other && state.locations[other]) out.push({ location: state.locations[other], edge: e });
  }
  return out;
}

/** Ajoute un lieu et ses voisins directs aux lieux connus du personnage. */
export function discover(state: GameState, c: Character, locationId: Id, withNeighbors = true): void {
  const add = (id: Id) => { if (!c.knownLocationIds.includes(id)) c.knownLocationIds.push(id); };
  add(locationId);
  if (withNeighbors) for (const n of neighbors(state, locationId)) add(n.location.id);
}

export function addCanon(state: GameState, now: Millis, text: string, tags: string[]): CanonFact {
  const fact: CanonFact = { id: newId(state, "fact"), text: text.slice(0, 300), tags: tags.slice(0, 6), at: now };
  state.canon.push(fact);
  if (state.canon.length > 500) state.canon.splice(0, state.canon.length - 500);
  return fact;
}

// ───────── Heure réelle (Europe/Paris) ─────────

const hourFmt = new Intl.DateTimeFormat("fr-FR", { timeZone: "Europe/Paris", hour: "numeric", minute: "numeric", hour12: false });
const longFmt = new Intl.DateTimeFormat("fr-FR", { timeZone: "Europe/Paris", weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" });
const dayKeyFmt = new Intl.DateTimeFormat("fr-CA", { timeZone: "Europe/Paris", year: "numeric", month: "2-digit", day: "2-digit" });

/** Heure décimale à Paris, ex. 22.5 pour 22 h 30. */
export function parisHour(now: Millis): number {
  const parts = hourFmt.formatToParts(new Date(now));
  const h = parseInt(parts.find((p) => p.type === "hour")?.value ?? "0", 10) % 24;
  const m = parseInt(parts.find((p) => p.type === "minute")?.value ?? "0", 10);
  return h + m / 60;
}

/** « AAAA-MM-JJ » du jour à Paris. */
export function parisDayKey(now: Millis): string {
  return dayKeyFmt.format(new Date(now));
}

/** Nuit de jeu = nuit réelle à Paris (22 h – 7 h) : les créatures sont plus dangereuses. */
export function isNight(now: Millis): boolean {
  const h = parisHour(now);
  return h >= 22 || h < 7;
}

/** Heures de sommeil des joueurs (23 h – 8 h) : les échéances ne s'écoulent pas. */
export function isQuietHours(now: Millis): boolean {
  const h = parisHour(now);
  return h >= 23 || h < 8;
}

export type DayPhase = "aube" | "jour" | "crepuscule" | "nuit";
export function dayPhase(now: Millis): DayPhase {
  const h = parisHour(now);
  if (h >= 6 && h < 8) return "aube";
  if (h >= 8 && h < 19) return "jour";
  if (h >= 19 && h < 22) return "crepuscule";
  return "nuit";
}

export function formatParisTime(now: Millis): string {
  const phase = dayPhase(now);
  const label = { aube: "aube", jour: "jour", crepuscule: "crépuscule", nuit: "nuit" }[phase];
  return `${longFmt.format(new Date(now))} (${label})`;
}

export function hoursBetween(a: Millis, b: Millis): number {
  return (b - a) / HOUR;
}

export function clamp(v: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, v));
}
